import React from "react";
import {
  CloudServerOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  HeartOutlined,
  GlobalOutlined,
  LockOutlined,
  TagOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Col, Row, Tag } from "antd";
import CommonCard from "../../../../global/card";
import { cn } from "../../../../../utils/cn";
import { formatDateTime } from "../../../../../utils/functions";

const STATUS_COLORS = {
  unassigned: "default",
  active: "green",
  flagged: "orange",
  suspended: "red",
};

const EnhancedStaticResidentialIpDetails = ({ detail }) => {
  if (!detail) return null;

  const getIconForField = (label) => {
    const iconMap = {
      Host: <GlobalOutlined />,
      Port: <GlobalOutlined />,
      Type: <TagOutlined />,
      Provider: <CloudServerOutlined />,
      Login: <LockOutlined />,
      Status: <InfoCircleOutlined />,
      "Assigned To": <UserOutlined />,
      "Lead Source": <TagOutlined />,
      Health: <HeartOutlined />,
      "Last Health Check": <CalendarOutlined />,
      "Last Used": <CalendarOutlined />,
      "Assigned At": <CalendarOutlined />,
      "Created At": <CalendarOutlined />,
      Notes: <FileTextOutlined />,
    };
    return iconMap[label] || <InfoCircleOutlined />;
  };

  const InfoItem = ({ label, value, isTag, tagColor, className }) => (
    <Col xs={24} sm={12}>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
          <span style={{ color: "var(--primary-color, #1890ff)" }}>
            {getIconForField(label)}
          </span>
          {label}
        </span>
        {isTag ? (
          <Tag
            color={tagColor || "blue"}
            className={cn("m-0 px-3 py-1 font-medium capitalize w-fit text-xs sm:text-sm", className)}
          >
            {value || "—"}
          </Tag>
        ) : (
          <span className={cn("text-sm sm:text-base font-semibold text-gray-900 break-all", className)}>
            {value || "—"}
          </span>
        )}
      </div>
    </Col>
  );

  return (
    <div className="sm:!p-6 p-2 w-full min-h-full overflow-x-hidden" style={{ backgroundColor: "#f5f7fa" }}>
      <div className="flex flex-col gap-6">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <CommonCard
              cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
              title={
                <div className="flex items-center gap-2">
                  <CloudServerOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: 18 }} />
                  <span style={{ fontSize: 16, fontWeight: 600 }}>Connection Details</span>
                </div>
              }
            >
              <Row gutter={[24, 16]} className="py-4">
                <InfoItem label="Host" value={detail.host} />
                <InfoItem label="Port" value={detail.port} />
                <InfoItem label="Type" value={(detail.type || "http").toUpperCase()} isTag tagColor="blue" />
                <InfoItem label="Provider" value={detail.provider || "webshare"} />
                <InfoItem label="Login" value={detail.login} />
              </Row>
            </CommonCard>
          </Col>

          <Col xs={24} lg={12}>
            <CommonCard
              cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
              title={
                <div className="flex items-center gap-2">
                  <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: 18 }} />
                  <span style={{ fontSize: 16, fontWeight: 600 }}>Assignment & Status</span>
                </div>
              }
            >
              <Row gutter={[24, 16]} className="py-4">
                <InfoItem
                  label="Status"
                  value={detail.status}
                  isTag
                  tagColor={STATUS_COLORS[detail.status] || "default"}
                />
                <InfoItem label="Assigned To" value={detail.assignedUserEmail} />
                <InfoItem label="Lead Source" value={detail.leadSource} />
                <InfoItem
                  label="Assigned At"
                  value={detail.assignedAt ? formatDateTime(detail.assignedAt) : null}
                />
              </Row>
            </CommonCard>
          </Col>

          <Col xs={24}>
            <CommonCard
              cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
              title={
                <div className="flex items-center gap-2">
                  <HeartOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: 18 }} />
                  <span style={{ fontSize: 16, fontWeight: 600 }}>Health & Activity</span>
                </div>
              }
            >
              <Row gutter={[24, 16]} className="py-4">
                {detail.lastHealthCheckResult ? (
                  <InfoItem
                    label="Health"
                    value={detail.lastHealthCheckResult}
                    isTag
                    tagColor={detail.lastHealthCheckResult === "pass" ? "green" : "red"}
                    className="uppercase"
                  />
                ) : (
                  <InfoItem label="Health" value={null} />
                )}
                <InfoItem
                  label="Last Health Check"
                  value={detail.lastHealthCheckAt ? formatDateTime(detail.lastHealthCheckAt) : null}
                />
                <InfoItem
                  label="Last Used"
                  value={detail.lastUsedAt ? formatDateTime(detail.lastUsedAt) : null}
                />
                <InfoItem
                  label="Created At"
                  value={detail.createdAt ? formatDateTime(detail.createdAt) : null}
                />
                <InfoItem label="Notes" value={detail.notes} />
              </Row>
            </CommonCard>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EnhancedStaticResidentialIpDetails;
